package com.kaihuoy.home.screens

import androidx.compose.runtime.*
import com.kaihuoy.shared.data.User
import com.kaihuoy.components.common.*
import com.kaihuoy.web.WebFirebaseRepository
import com.kaihuoy.components.common.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun ProfileScreen(user: User?) {
    val repository = remember { WebFirebaseRepository() }
    var isEditing by remember { mutableStateOf(false) }
    var isSaving by remember { mutableStateOf(false) }
    var isSendingResetEmail by remember { mutableStateOf(false) }

    var displayName by remember { mutableStateOf("") }
    var birthDate by remember { mutableStateOf("") }
    var employer by remember { mutableStateOf("") }
    var taxNumber by remember { mutableStateOf("") }
    var address by remember { mutableStateOf("") }
    var phone by remember { mutableStateOf("") }

    LaunchedEffect(user, isEditing) {
        if (user != null && !isEditing) {
            displayName = user.displayName
            birthDate = user.birthDate
            employer = user.employer
            taxNumber = user.taxNumber
            address = user.address
            phone = user.phone
        }
    }

    fun resetForm() {
        if (user != null) {
            displayName = user.displayName
            birthDate = user.birthDate
            employer = user.employer
            taxNumber = user.taxNumber
            address = user.address
            phone = user.phone
        }
    }

    Div(attrs = { style { padding(32.px, screenContentPadding()) } }) {
        H1(attrs = { style { marginBottom(8.px); color(KaihuTheme.TextPrimary) } }) {
            Text("Profiilisi")
        }

        P(attrs = {
            style {
                marginBottom(32.px)
                color(KaihuTheme.TextSecondary)
                fontSize(1.05.em)
            }
        }) {
            Text("Täällä näet ja voit muokata omia tietojasi.")
        }

        // Käyttäjätiedot-kortti
        Div(attrs = {
            style {
                property("background", KaihuTheme.CardGradient)
                padding(28.px)
                borderRadius(16.px)
                property("border", "1px solid ${KaihuTheme.Border}")
                property("box-shadow", KaihuTheme.ShadowMd)
                marginBottom(32.px)
            }
        }) {
            if (user != null) {
                // Roolitagit
                Div(attrs = { style { display(DisplayStyle.Flex); gap(8.px); flexWrap(FlexWrap.Wrap); marginBottom(24.px) } }) {
                    if (user.isAdmin) RoleBadge("👑 Ylläpitäjä", "#7C3AED", "#EDE9FE", "#4C1D95")
                    if (user.isHuolto) RoleBadge("🔧 Huolto", "#0369A1", "#E0F2FE", "#075985")
                    if (!user.isAdmin && !user.isHuolto) RoleBadge("👤 Peruskäyttäjä", "#374151", "#F1F5F9", "#1E293B")
                }

                // Tietoruudukko
                Div(attrs = {
                    style {
                        property("display", "grid")
                        property("grid-template-columns", "repeat(auto-fill, minmax(220px, 1fr))")
                        gap(16.px)
                    }
                }) {
                    listOf(
                        "👤 Nimi" to user.displayName,
                        "✉️ Sähköposti" to user.email,
                        "🏢 Työnantaja" to user.employer.ifEmpty { "-" },
                        "📞 Puhelin" to user.phone.ifEmpty { "-" },
                        "📍 Osoite" to user.address.ifEmpty { "-" },
                        "🎂 Syntymäaika" to user.birthDate.ifEmpty { "-" },
                        "🔢 Veronumero" to user.taxNumber.ifEmpty { "-" }
                    ).forEach { (label, value) ->
                        Div(attrs = {
                            style {
                                backgroundColor(KaihuTheme.Background); borderRadius(10.px); padding(14.px)
                                property("border", "1px solid ${KaihuTheme.Border}")
                            }
                        }) {
                            Div(attrs = { style { fontSize(0.75.em); color(KaihuTheme.TextSecondary); fontWeight("600"); property("text-transform", "uppercase"); property("letter-spacing", "0.05em"); marginBottom(4.px) } }) { Text(label) }
                            Div(attrs = { style { fontSize(0.95.em); color(KaihuTheme.TextPrimary); fontWeight("500") } }) { Text(value) }
                        }
                    }
                }
            } else {
                P(attrs = { style { color(KaihuTheme.TextSecondary) } }) { Text("Käyttäjätietoja ei löytynyt") }
            }
        }

        if (isEditing && user != null) {
            Div(attrs = {
                style {
                    property("background", KaihuTheme.CardGradient)
                    padding(28.px)
                    borderRadius(16.px)
                    property("border", "1px solid ${KaihuTheme.Border}")
                    property("box-shadow", KaihuTheme.ShadowMd)
                    marginBottom(32.px)
                }
            }) {
                H3(attrs = { style { marginTop(0.px); marginBottom(20.px); color(KaihuTheme.TextPrimary) } }) {
                    Text("Päivitä tietosi")
                }

                StyledInput(label = "Nimi", value = displayName, onValueChange = { displayName = it })
                StyledInput(label = "Työnantaja", value = employer, onValueChange = { employer = it })
                StyledInput(label = "Puhelin", value = phone, onValueChange = { phone = it })
                StyledInput(label = "Osoite", value = address, onValueChange = { address = it })
                StyledDateInput(label = "Syntymäaika", value = birthDate, onValueChange = { birthDate = it })
                StyledInput(label = "Veronumero", value = taxNumber, onValueChange = { taxNumber = it })

                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        gap(12.px)
                        flexWrap(FlexWrap.Wrap)
                        marginTop(8.px)
                    }
                }) {
                    StyledButton(
                        text = if (isSaving) "Tallennetaan..." else "Tallenna tiedot",
                        enabled = !isSaving,
                        onClick = {
                            if (displayName.trim().isBlank()) {
                                com.kaihuoy.components.common.AppFeedback.showError("Nimi ei voi olla tyhjä.")
                                return@StyledButton
                            }

                            isSaving = true
                            val updatedUser = user.copy(
                                displayName = displayName.trim(),
                                employer = employer.trim(),
                                phone = phone.trim(),
                                address = address.trim(),
                                birthDate = birthDate.trim(),
                                taxNumber = taxNumber.trim()
                            )

                            repository.saveUser(updatedUser) { success ->
                                isSaving = false
                                if (success) {
                                    isEditing = false
                                    com.kaihuoy.components.common.AppFeedback.showSuccess("Omat tiedot tallennettu.")
                                } else {
                                    com.kaihuoy.components.common.AppFeedback.showError("Tietojen tallennus epäonnistui.")
                                }
                            }
                        }
                    )

                    StyledButton(
                        text = "Peruuta",
                        backgroundColor = KaihuTheme.Grey,
                        enabled = !isSaving,
                        onClick = {
                            resetForm()
                            isEditing = false
                        }
                    )
                }
            }
        }

        // Toiminnot
        Div(attrs = {
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("repeat(auto-fill, minmax(min(280px, 100%), 1fr))")
                gap(20.px)
            }
        }) {
            StyledButton(
                text = if (isEditing) "Peruuta muokkaus" else "Muokkaa tietoja",
                enabled = user != null && !isSaving,
                onClick = {
                    if (isEditing) {
                        resetForm()
                    }
                    isEditing = !isEditing
                }
            )

            StyledButton(
                text = if (isSendingResetEmail) "Lähetetään..." else "Vaihda salasana",
                enabled = user?.email?.isNotBlank() == true && !isSendingResetEmail,
                onClick = {
                    val email = user?.email?.trim().orEmpty()
                    if (email.isBlank()) {
                        com.kaihuoy.components.common.AppFeedback.showError("Käyttäjällä ei ole sähköpostiosoitetta salasanan vaihtoa varten.")
                        return@StyledButton
                    }

                    isSendingResetEmail = true
                    repository.sendPasswordResetEmail(email) { success, errorMessage ->
                        isSendingResetEmail = false
                        if (success) {
                            com.kaihuoy.components.common.AppFeedback.showSuccess("Salasanan vaihtolinkki lähetettiin osoitteeseen $email")
                        } else {
                            com.kaihuoy.components.common.AppFeedback.showError(errorMessage ?: "Salasanan vaihtolinkin lähetys epäonnistui.")
                        }
                    }
                }
            )
        }

        // Info-alue
        Div(attrs = { style { marginTop(40.px); opacity(0.8) } }) {
            P(attrs = { style { color(KaihuTheme.TextSecondary) } }) {
                Text("Tiedot näkyvät mm. perehdytyslomakkeissa. Salasanan vaihto onnistuu helposti — lähetämme sinulle linkin sähköpostiin.")
            }
        }
    }
}

@Composable
private fun RoleBadge(label: String, borderColor: String, bgColor: String, textColor: String) {
    Span(attrs = {
        style {
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(6.px)
            padding(6.px, 14.px); borderRadius(999.px)
            property("background", "linear-gradient(135deg, ${bgColor}22 0%, ${bgColor}44 100%)")
            property("border", "1px solid $borderColor")
            color(Color(borderColor)); fontWeight("700"); fontSize(0.82.em)
            property("letter-spacing", "0.03em")
            property("box-shadow", "0 2px 8px ${borderColor}44")
        }
    }) { Text(label) }
}